import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../../utils/api';
import { AuthBrandMark } from './AuthBrandMark';

/**
 * AI-vbof (Feature B, Gap 2) — self-service "Forgot password" email entry.
 *
 * The backend `POST /auth/forgot-password` is anti-enumeration: it ALWAYS
 * returns 200 with the same message whether or not the email exists. So this
 * page shows the same confirmation regardless of the API response. The link
 * lives on the login page (`LoginPage.tsx`).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (err: any) {
      // A 429 (rate-limited) is the one signal we surface to the user. Any
      // other error still falls through to the same anti-enumeration notice.
      if (err?.response?.status === 429) {
        setError(err.response?.data?.error || 'Too many reset requests. Please try again later.');
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(false);
    setDone(true);
  };

  return (
    <div
      className="aura-root flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--ivory-0)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <AuthBrandMark />
          <h1
            className="mt-4 text-2xl"
            style={{ fontFamily: 'var(--ff-serif)', fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}
          >
            Reset your password
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
            Enter your email and we'll send a reset link
          </p>
        </div>

        <div
          className="rounded-xl p-8"
          style={{ background: 'var(--ivory-1)', border: '1px solid var(--line)', boxShadow: 'var(--sh-2)' }}
        >
          {done ? (
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--ink-1)' }}>
                If an account exists for <span style={{ fontWeight: 600 }}>{email}</span>, we've sent a reset link.
              </p>
              <p className="mt-2 text-xs" style={{ color: 'var(--ink-3)' }}>
                The link expires in 30 minutes.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-semibold"
                style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)' }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div
                  className="mb-4 rounded-lg p-3 text-sm"
                  style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
                >
                  {error}
                </div>
              )}
              <div className="mb-6">
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
                  style={{ background: 'var(--ivory-0)', color: 'var(--ink-0)', border: '1px solid var(--line)' }}
                  placeholder="you@company.com"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)' }}
              >
                {submitting ? <Loader2 size={16} className="mx-auto animate-spin" /> : 'Send reset link'}
              </button>
            </form>
          )}

          <p className="mt-4 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            <Link to="/login" className="font-medium" style={{ color: 'var(--saffron-600)' }}>
              Back to sign in
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">Powered by Ardhanex Tech Pvt Ltd</p>
      </div>
    </div>
  );
}
